import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CustomTextField } from "../../Layout/CustomTextField";
import { downloadFile, formatDate } from "../../../services/shared";
import { createHubspotNotes, getHubspotNotes } from "../../../utilities/Response";
import DOMPurify from 'dompurify';
import { CircularProgress } from "@mui/material";

interface TicketDetails {
  id: string;
  createdAt: string;
  subject: string;
  priority: string;
  category: any;
  content: string;
  status: string;
  pipelineStage: string;
  name?: string;
  email?: string
}

const Detail = (props: { ticket: TicketDetails }) => {


  const [notes, setNotes] = useState<Notes[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const design = useSelector((state: any) => state?.design);
  const [apiInProgress, setAPIInProgress] = useState<boolean>(false);

  function transformString(input: string) {
    // Replace underscores with spaces
    let stringWithSpaces = input.replace(/_/g, ' ');

    // Convert to title case
    let titleCaseString = stringWithSpaces.toLowerCase().split(' ').map(function (word) {
      return word.replace(word[0], word[0].toUpperCase());
    }).join(' ');

    return titleCaseString;
  }

  const getNotes = useCallback(() => {
    getHubspotNotes(props.ticket.id)
      .then((notes: { results: Notes[] }) => {
        setNotes((notes.results || []).reverse());
      }).catch(err => console.log(err));

  }, [props.ticket.id]);

  useEffect(() => {
    getNotes();
  }, [getNotes]);

  const HtmlContent: React.FC<HtmlContentProps> = ({ htmlContent }) => {
    const cleanHtml = DOMPurify.sanitize(htmlContent);
    return (
      <div className="break-words prose" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
    );
  };

  const isValidNewNote = () => {
    return !!newNote && !!newNote.trim().length;
  }

  const addNewNote = () => {
    if (!isValidNewNote() || apiInProgress) {
      return false;
    }

    setAPIInProgress(true);

    createHubspotNotes(props.ticket.id, newNote)
      .then((data: any) => {
        if (!data || (data.name && data.name === 'AxiosError')) {
          throw new Error('Failed');
        }
        setNotes(notes => [{ ...data, isNew: true }, ...notes])
        setNewNote('');
        scrollBottom();
      })
      .catch(err => console.log(err))
      .finally(() => setAPIInProgress(false));
  }

  const scrollBottom = () => {
    try {

      if ((window as any).notesRef && (window as any).notesRef.current) {
        (window as any).notesRef.current.scrollTo({
          top: (window as any).notesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    } catch (e) {
      console.log(e);
    }
  }

  return (
    <>
      <div id='ele-100' className="relative flex flex-col overflow-y-auto" >

        <div id='ele-101' className="text-primaryText text-lg font-mSemiBold mt-4 px-2">Ticket Details</div>

        <div id='ele-102' className="flex flex-col">
          <div id='ele-103' className="flex justify-between w-full my-2 px-2">
            <div id='ele-104' className="text-primaryText text-sm font-bold">Status: </div>
            <div id='ele-105' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.pipelineStage} </div>
          </div>

          <div id='ele-106' className="flex justify-between w-full my-2 px-2">
            <div id='ele-107' className="text-primaryText text-sm font-bold">Ticket ID: </div>
            <div id='ele-108' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.id} </div>
          </div>

          <div id='ele-109' className="flex justify-between w-full my-2 px-2">
            <div id='ele-110' className="text-primaryText text-sm font-bold">Full Name: </div>
            <div id='ele-111' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.name} </div>
          </div>

          <div id='ele-112' className="flex justify-between w-full my-2 px-2">
            <div id='ele-113' className="text-primaryText text-sm font-bold">Email: </div>
            <div id='ele-114' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.email} </div>
          </div>

          {props.ticket.category &&
            <div id='ele-115' className="flex justify-between w-full my-2 px-2">
              <div id='ele-116' className="text-primaryText text-sm font-bold">Issue Category: </div>
              <div id='ele-117'className="text-secondaryText w-1/2 text-sm font-thin text-right">{transformString(props.ticket.category)} </div>
            </div>
          }

          <div id='ele-118' className="flex justify-between w-full my-2 px-2">
            <div id='ele-119' className="text-primaryText text-sm font-bold">Issue Title: </div>
            <div id='ele-120' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.subject} </div>
          </div>

          <div id='ele-121' className="flex justify-between w-full my-2 px-2">
            <div id='ele-122' className="text-primaryText text-sm font-bold">Issue Description: </div>
            <div id='ele-123' className="text-secondaryText w-1/2 text-sm font-thin text-right">{props.ticket.content} </div>
          </div>
        </div>

        {props.ticket.status !== 'CLOSED' ?
          <>
            <div id='ele-124' className="text-primaryText text-lg font-mSemiBold mt-4 px-2">Add Comments</div>

            <div className="mt-3 px-2" id='ele-125'>
              <CustomTextField design={design}
                placeholder='Write your comment' multiline rows={4}
                value={newNote}
                inputProps={{ maxLength: 2000 }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewNote(e.target.value)}
              />
            </div>

            <div id='ele-126' className="flex justify-end mt-3 px-2">
              <div
                className={`align-middle justify-center items-center text-white rounded-lg px-6 py-2 ml-4 ${!isValidNewNote() ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
                style={{ backgroundColor: design?.themeColor }}
                onClick={() => addNewNote()}
                id='ele-127'
              >
                Add Comment
                <span id='ele-128' className="ml-2 ">{apiInProgress && <CircularProgress size={12} sx={{ color: 'white' }} />}</span>
              </div>

            </div>
          </>
          : <>{!!notes && !!notes.length && <hr id='ele-129' className="mt-2" />}</>
        }

        {!!notes && !!notes.length &&
          <>
            <div id='ele-130' className="text-primaryText text-lg font-mSemiBold mt-4 px-2">Comments</div>

            <div className="flex flex-col w-full px-2 mt-2" id='ele-131'>
              {notes.map((note) => {
                return <div id='ele-132' key={'nid' + note.id} className="flex flex-col mb-4">
                  <div id='ele-133' className="flex justify-between item-center align-middle mb-1">
                    <div id='ele-134'>
                      <span id='ele-135' className="font-mSemiBold text-sm">{!note.createdBy ? 'You' : 'Agent'}</span>  &nbsp; &nbsp;
                      <span id='ele-136' className="text-lightText text-sm">{formatDate(note.createdAt)}</span>
                    </div>
                    {note.isNew && <div id='ele-137' className="w-3 h-3 rounded-full my-auto" style={{ backgroundColor: design?.themeColor }}></div>}
                  </div>
                  <div id='ele-138' className="text-footer text-sm "><HtmlContent htmlContent={note.content} /></div>
                  {note.attachments && note.attachments.length &&
                    <div id='ele-139' className="flex flex-col justify-start my-2">
                      {note.attachments.map(attachment => {
                        return <div className="ele-ticket-attachment-container w-full flex justify-start align-middle items-center mt-2" key={attachment.id}>
                          <img src={attachment.url} alt="img" width={100} height={100} className="object-cover border cursor-pointer ele-ticket-attachement-img"
                            onClick={() => downloadFile(attachment.url, attachment.name)} />
                          <div className="text-sm w-2/3  ml-2 truncate ele-ticket-attachment-name">{attachment.name}.png</div>
                        </div>
                      })}
                    </div>
                  }
                </div>
              })}
            </div>
          </>
        }
      </div>
    </>
  );
}

export default memo(Detail);

export interface Notes {
  id: string;
  content: string;
  createdAt: string;
  archived: boolean;
  updatedAt: string;
  createdBy: any;
  attachments: {
    id: string;
    name: string;
    url: string;
  }[];
  isNew?: boolean;
}
interface HtmlContentProps {
  htmlContent: string;
}